import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../api/axios";

const GestionConvenios = () => {
  const navigate = useNavigate();

  const [alerta, setAlerta] = useState({
    mostrar: false,
    mensaje: "",
    tipo: "success",
  });

  const [convenios, setConvenios] = useState([]);
  const [loading, setLoading] = useState(true);

  const [verInactivos, setVerInactivos] = useState(false);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [editando, setEditando] = useState(false);
  const [convenioId, setConvenioId] = useState(null);

  const [mostrarModalEstado, setMostrarModalEstado] = useState(false);
  const [convenioParaCambiarEstado, setConvenioParaCambiarEstado] = useState(null);

  // Estado del formulario
  const [nuevoConvenio, setNuevoConvenio] = useState({
    nombre: "",
    descripcion: "",
    detalles: "",
    estado: "activo",
  });

  // Estados para Cloudinary (logo)
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const mostrarAlerta = (mensaje, tipo = "success") => {
    setAlerta({
      mostrar: true,
      mensaje,
      tipo,
    });

    setTimeout(() => {
      setAlerta({ mostrar: false, mensaje: "", tipo: "success" });
    }, 3000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNuevoConvenio((prev) => ({ ...prev, [name]: value }));
  };

  // ==================== DRAG & DROP ====================
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      processFile(file);
    } else {
      mostrarAlerta("Solo se permiten archivos de imagen", "danger");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) processFile(file);
  };

  const processFile = (file) => {
    if (file.size > 5 * 1024 * 1024) {
      mostrarAlerta("La imagen no debe superar los 5MB", "danger");
      return;
    }
    setLogoFile(file);
    const previewUrl = URL.createObjectURL(file);
    setLogoPreview(previewUrl);
  };

  const removeLogo = (e) => {
    if (e) e.stopPropagation();
    setLogoFile(null);
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoPreview(null);
  };

  // ==================== GUARDAR CONVENIO ====================
  const guardarConvenio = async () => {
    if (!nuevoConvenio.nombre.trim() || !nuevoConvenio.descripcion.trim()) {
      mostrarAlerta("Nombre y descripción son obligatorios", "danger");
      return;
    }

    const formData = new FormData();
    formData.append("nombre", nuevoConvenio.nombre.trim());
    formData.append("descripcion", nuevoConvenio.descripcion.trim());
    formData.append("detalles", nuevoConvenio.detalles.trim() || "");
    formData.append("estado", nuevoConvenio.estado);

    if (logoFile) {
      formData.append("logo", logoFile);
    }

    const url = editando ? `/convenios/${convenioId}` : "/convenios";
    const method = editando ? "put" : "post";

    try {
      await axios({
        method,
        url,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      setMostrarModal(false);
      resetForm();
      mostrarAlerta(editando ? "Convenio actualizado correctamente" : "Convenio creado correctamente");
      cargarConvenios();
    } catch (error) {
      console.error("Error al guardar convenio:", error);
      mostrarAlerta(
        error.response?.data?.message || "Error al guardar el convenio",
        "danger"
      );
    }
  };

  const resetForm = () => {
    setEditando(false);
    setConvenioId(null);
    setNuevoConvenio({
      nombre: "",
      descripcion: "",
      detalles: "",
      estado: "activo",
    });
    setLogoFile(null);
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoPreview(null);
  };

  // Preparar edición
  const abrirEditar = (convenio) => {
    setNuevoConvenio({
      nombre: convenio.nombre,
      descripcion: convenio.descripcion,
      detalles: convenio.detalles || "",
      estado: convenio.estado || "activo",
    });
    setConvenioId(convenio.id);
    setEditando(true);
    setLogoFile(null);
    setLogoPreview(convenio.logo || null);
    setMostrarModal(true);
  };

  // Cargar convenios
  const cargarConvenios = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/convenios");
      setConvenios(response.data.convenios || []);
    } catch (error) {
      console.error("Error al cargar convenios:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarConvenios();
  }, []);

  // Lista filtrada
  const conveniosFiltrados = convenios.filter((c) =>
    verInactivos ? c.estado === "inactivo" : c.estado !== "inactivo"
  );

  // Cambiar estado
  const abrirModalEstado = (convenio) => {
    setConvenioParaCambiarEstado(convenio);
    setMostrarModalEstado(true);
  };

  const confirmarCambiarEstado = async () => {
    if (!convenioParaCambiarEstado) return;

    try {
      const { data } = await axios.put(
        `/convenios/${convenioParaCambiarEstado.id}/estado`
      );

      setConvenios((prev) =>
        prev.map((c) =>
          c.id === convenioParaCambiarEstado.id ? { ...c, estado: data.convenio.estado } : c
        )
      );

      mostrarAlerta(data.message || "Estado actualizado correctamente");
      setMostrarModalEstado(false);
      setConvenioParaCambiarEstado(null);
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      mostrarAlerta(
        error.response?.data?.message || "No se pudo cambiar el estado",
        "danger"
      );
    }
  };

  return (
    <div className="container-fluid bg-light min-vh-100 py-4 px-3 px-md-4 px-lg-5">
      {/* Alerta flotante */}
      {alerta.mostrar && (
        <div
          className={`alert alert-${alerta.tipo} alert-dismissible fade show shadow position-fixed`}
          style={{
            top: "20px",
            right: "20px",
            zIndex: 9999,
            minWidth: "300px",
          }}
        >
          <div className="d-flex align-items-center">
            <i
              className={`bi me-2 ${
                alerta.tipo === "success"
                  ? "bi-check-circle-fill"
                  : "bi-x-circle-fill"
              }`}
            ></i>
            <span>{alerta.mensaje}</span>
          </div>
          <button
            className="btn-close"
            onClick={() => setAlerta({ ...alerta, mostrar: false })}
          ></button>
        </div>
      )}

      {/* Header */}
      <div className="card shadow-sm border-0 mb-4 border-start border-success border-4 rounded-3">
        <div className="card-body d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div className="d-flex align-items-center gap-3">
            <button
              className="btn btn-outline-success border-0 rounded-circle p-2"
              onClick={() => navigate("/DashboardSecretaria")}
              title="Volver al dashboard"
            >
              <i className="bi bi-arrow-left-short fs-4"></i>
            </button>
            <h4 className="fw-bold mb-0 text-dark">
              <i className="bi bi-handshake me-2 text-success"></i>
              Gestión de Convenios
            </h4>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button
              className={`btn d-flex align-items-center gap-2 shadow-sm px-3 ${
                verInactivos ? "btn-success" : "btn-outline-secondary"
              }`}
              onClick={() => setVerInactivos((prev) => !prev)}
            >
              <i className={`bi ${verInactivos ? "bi-eye" : "bi-eye-slash"}`}></i>
              {verInactivos ? "Ver activos" : "Ver inactivos"}
            </button>

            <button
              className="btn btn-success d-flex align-items-center gap-2 shadow-sm px-3"
              onClick={() => {
                resetForm();
                setMostrarModal(true);
              }}
            >
              <i className="bi bi-plus-lg"></i>
              Nuevo Convenio
            </button>
          </div>
        </div>
      </div>

      {/* Tabla de convenios */}
      <div className="card shadow-sm border-0 rounded-3 overflow-hidden">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light text-uppercase small text-muted">
                  <tr>
                    <th className="ps-4 border-0">Nombre</th>
                    <th className="border-0">Descripción</th>
                    <th className="border-0">Detalles</th>
                    <th className="border-0">Logo</th>
                    <th className="border-0">Estado</th>
                    <th className="text-end pe-4 border-0">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {conveniosFiltrados.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-5 text-muted">
                        <i className="bi bi-handshake fs-1 d-block mb-3 text-success opacity-50"></i>
                        {verInactivos
                          ? "No hay convenios inactivos"
                          : "No hay convenios activos registrados aún"}
                      </td>
                    </tr>
                  ) : (
                    conveniosFiltrados.map((c) => (
                      <tr key={c.id}>
                        <td className="ps-4 fw-medium">{c.nombre}</td>
                        <td className="text-secondary">{c.descripcion}</td>
                        <td style={{ whiteSpace: "pre-line" }}>
                          {c.detalles || <span className="text-muted fst-italic">—</span>}
                        </td>
                        <td>
                          {c.logo ? (
                            <img
                              src={c.logo}
                              alt={c.nombre}
                              className="rounded shadow-sm"
                              style={{
                                width: "64px",
                                height: "48px",
                                objectFit: "contain",
                                background: "#f8f9fa",
                                border: "1px solid #e0e0e0",
                              }}
                              onError={(e) => {
                                e.target.src = "https://via.placeholder.com/64x48?text=Logo";
                              }}
                            />
                          ) : (
                            <span className="text-muted fst-italic">Sin logo</span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`badge rounded-pill px-3 py-2 ${
                              c.estado === "inactivo" ? "bg-secondary" : "bg-success"
                            }`}
                          >
                            {c.estado === "inactivo" ? "Inactivo" : "Activo"}
                          </span>
                        </td>
                        <td className="text-end pe-4">
                          <button
                            className="btn btn-sm btn-outline-success me-2"
                            onClick={() => abrirEditar(c)}
                            title="Editar convenio"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className={`btn btn-sm ${
                              c.estado === "inactivo" ? "btn-outline-success" : "btn-outline-danger"
                            }`}
                            onClick={() => abrirModalEstado(c)}
                            title={c.estado === "inactivo" ? "Activar convenio" : "Desactivar convenio"}
                          >
                            <i className={`bi ${c.estado === "inactivo" ? "bi-toggle2-off" : "bi-toggle2-on"}`}></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ==================== MODAL CREAR / EDITAR ==================== */}
      {mostrarModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.55)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content rounded-4 shadow border-0">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-success">
                  {editando ? "Editar Convenio" : "Nuevo Convenio"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setMostrarModal(false);
                    resetForm();
                  }}
                ></button>
              </div>

              <div className="modal-body px-4 pb-4">
                <div className="mb-3">
                  <label className="form-label fw-medium">Nombre del convenio</label>
                  <input
                    className="form-control"
                    placeholder="Ej: Descuento Farmacia XYZ"
                    name="nombre"
                    value={nuevoConvenio.nombre}
                    onChange={handleChange}
                    autoFocus
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-medium">Descripción</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Detalles del beneficio, condiciones, etc."
                    name="descripcion"
                    value={nuevoConvenio.descripcion}
                    onChange={handleChange}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-medium">Detalles (uno por línea)</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    placeholder="Producto 1&#10;Producto 2 con 20%&#10;Válido hasta 31/12/2025"
                    name="detalles"
                    value={nuevoConvenio.detalles}
                    onChange={handleChange}
                  />
                </div>

                {/* Logo con Drag & Drop */}
                <div className="mb-4">
                  <label className="form-label fw-medium">Logo del convenio</label>
                  <div
                    className={`border border-2 border-dashed rounded-3 p-4 text-center ${
                      isDragging ? "border-success bg-success bg-opacity-10" : "border-secondary"
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById("logoInput").click()}
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="file"
                      id="logoInput"
                      accept="image/*"
                      className="d-none"
                      onChange={handleFileSelect}
                    />

                    {logoPreview ? (
                      <div className="position-relative d-inline-block">
                        <img
                          src={logoPreview}
                          alt="Vista previa"
                          className="img-fluid rounded shadow-sm"
                          style={{ maxHeight: "160px", objectFit: "contain" }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1"
                          onClick={removeLogo}
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      </div>
                    ) : (
                      <div>
                        <i className="bi bi-cloud-upload fs-1 text-muted mb-2 d-block"></i>
                        <p className="mb-1 fw-medium">Arrastra tu imagen aquí o haz clic para seleccionar</p>
                        <small className="text-muted">PNG, JPG, WEBP (máx. 5MB)</small>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-medium">Estado</label>
                  <select
                    className="form-select"
                    name="estado"
                    value={nuevoConvenio.estado}
                    onChange={handleChange}
                  >
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                  </select>
                </div>
              </div>

              <div className="modal-footer border-0 px-4 pb-4">
                <button
                  className="btn btn-outline-secondary px-4"
                  onClick={() => {
                    setMostrarModal(false);
                    resetForm();
                  }}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-success px-4 d-flex align-items-center gap-2"
                  onClick={guardarConvenio}
                >
                  <i className="bi bi-check-lg"></i>
                  {editando ? "Actualizar" : "Guardar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== MODAL CAMBIAR ESTADO ==================== */}
      {mostrarModalEstado && convenioParaCambiarEstado && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.55)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content rounded-3 shadow border-0">
              <div
                className={`modal-header text-white ${
                  convenioParaCambiarEstado.estado === "inactivo" ? "bg-success" : "bg-danger"
                }`}
              >
                <h5 className="modal-title fw-bold">
                  {convenioParaCambiarEstado.estado === "inactivo"
                    ? "Activar Convenio"
                    : "Desactivar Convenio"}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setMostrarModalEstado(false)}
                ></button>
              </div>

              <div className="modal-body">
                <p className="mb-0">
                  ¿Estás seguro de que deseas{" "}
                  <strong>
                    {convenioParaCambiarEstado.estado === "inactivo" ? "activar" : "desactivar"}
                  </strong>{" "}
                  el convenio <strong>{convenioParaCambiarEstado.nombre}</strong>?
                </p>
              </div>

              <div className="modal-footer border-0">
                <button
                  className="btn btn-outline-secondary px-4"
                  onClick={() => setMostrarModalEstado(false)}
                >
                  Cancelar
                </button>
                <button
                  className={`btn px-4 ${
                    convenioParaCambiarEstado.estado === "inactivo" ? "btn-success" : "btn-danger"
                  }`}
                  onClick={confirmarCambiarEstado}
                >
                  {convenioParaCambiarEstado.estado === "inactivo" ? "Activar" : "Desactivar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionConvenios;